import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
  type ChartOptions,
  type ChartType,
  type Plugin,
  type TooltipPositionerFunction,
} from 'chart.js';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SegmentButton } from '@/components/ui/segment-button';
import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/lib/theme';
import { cn } from '@/lib/utils';

import {
  coalesceAgents,
  DEFAULT_CHART_AGENT_COUNT,
  DEFAULT_CHART_CATEGORY_COUNT,
  rankCategoriesByComparison,
  rankCategoriesByDiscrimination,
  sampleOf,
  shareOfFindings,
  type ChartAgent,
} from '../agent-category-charts';
import {
  compareRates,
  SMALL_SAMPLE,
  whiskerHalfWidths,
  type RateComparison,
} from '../agent-rate-stats';

import type {
  AgentCategoryCount,
  AgentCount,
  OnlineEvalAgentCountsResponse,
  OnlineEvalChartSettings,
  OnlineEvalChartUnit,
} from '../online-eval.types';

type WhiskerDataset = { whiskers?: number[] };

// Draws a whisker through each bar's top, from the dataset's `whiskers` half-widths
const whiskerPlugin: Plugin<'bar'> = {
  id: 'whiskers',
  afterDatasetsDraw(chart) {
    const dataset = chart.data.datasets[0] as WhiskerDataset | undefined;
    const whiskers = dataset?.whiskers;
    if (!whiskers) return;
    const meta = chart.getDatasetMeta(0);
    const yScale = chart.scales.y;
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.strokeStyle =
      (chart.options.scales?.y?.ticks?.color as string) ?? '#888';
    ctx.lineWidth = 1.5;
    meta.data.forEach((element, index) => {
      const halfWidth = whiskers[index];
      const value = chart.data.datasets[0].data[index];
      if (!halfWidth || typeof value !== 'number') return;
      const x = element.x;
      const top = Math.max(
        chartArea.top,
        yScale.getPixelForValue(value + halfWidth)
      );
      const bottom = Math.min(
        chartArea.bottom,
        yScale.getPixelForValue(Math.max(0, value - halfWidth))
      );
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.moveTo(x - 5, top);
      ctx.lineTo(x + 5, top);
      ctx.moveTo(x - 5, bottom);
      ctx.lineTo(x + 5, bottom);
      ctx.stroke();
    });
    ctx.restore();
  },
};

// The hovered bar's top is what the other whiskers are read against, so the tooltip
// must not sit on it: it goes to the top corner farthest from the pointer instead
const farCorner: TooltipPositionerFunction<ChartType> = function (
  _elements,
  eventPosition
) {
  const { left, right, top } = this.chart.chartArea;
  const pointerOnLeft = (eventPosition.x ?? left) < (left + right) / 2;
  return {
    x: pointerOnLeft ? right : left,
    y: top,
    xAlign: pointerOnLeft ? 'right' : 'left',
    yAlign: 'top',
  };
};
Tooltip.positioners.farCorner = farCorner;

declare module 'chart.js' {
  interface TooltipPositionerMap {
    farCorner: TooltipPositionerFunction<ChartType>;
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  whiskerPlugin
);

type ThemeTokens = {
  grid: string;
  tick: string;
  series: string[];
};

// Categorical slots in fixed order, stepped per surface, so an agent keeps its hue across charts
const TOKENS: Record<ThemeMode, ThemeTokens> = {
  light: {
    grid: 'rgba(0, 0, 0, 0.07)',
    tick: '#6b7280',
    series: [
      '#2a78d6',
      '#eb6834',
      '#1baf7a',
      '#eda100',
      '#e87ba4',
      '#008300',
      '#4a3aa7',
      '#e34948',
    ],
  },
  dark: {
    grid: 'rgba(255, 255, 255, 0.07)',
    tick: '#9ca3af',
    series: [
      '#3987e5',
      '#d95926',
      '#199e70',
      '#c98500',
      '#d55181',
      '#008300',
      '#9085e9',
      '#e66767',
    ],
  },
};

type UnitCopy = {
  label: string;
  noun: string;
  countOf: (row: AgentCount | AgentCategoryCount) => number;
  description: string;
};

const UNIT_COPY: Record<OnlineEvalChartUnit, UnitCopy> = {
  chat: {
    label: 'Per chat',
    noun: 'evaluated chats',
    countOf: (row) => row.chat_count,
    description:
      'Each bar is the share of the evaluated chats an agent replied in that have a finding in the category on one of its replies.',
  },
  message: {
    label: 'Per message',
    noun: 'evaluated messages',
    countOf: (row) => row.message_count,
    description:
      "Each bar is the share of an agent's evaluated replies that have a finding in the category. Findings persisted before evaluated messages were tracked are left out.",
  },
};

const SIGNIFICANCE_LEVEL = 0.05;

const seriesColor = (
  tokens: ThemeTokens,
  agentIndex: number,
  agent: ChartAgent
): string => {
  const color = tokens.series[agentIndex % tokens.series.length];
  // Small samples are drawn faded so a tall bar on a handful of chats does not read as a finding
  return agent.total < SMALL_SAMPLE ? `${color}66` : color;
};

const formatPercent = (share: number): string => `${(share * 100).toFixed(1)}%`;

const formatPoints = (share: number): string =>
  `${share >= 0 ? '+' : ''}${(share * 100).toFixed(1)}`;

const formatPValue = (pValue: number): string =>
  pValue < 0.0001 ? 'p < 0.0001' : `p = ${pValue.toFixed(4)}`;

const truncateLabel = (label: string): string =>
  label.length > 18 ? `${label.slice(0, 16)}…` : label;

// Each chart's axis tops out at 2x its own tallest bar, so the top half stays
// clear for the tooltip and small shares stay readable
const AXIS_HEADROOM = 2;

const axisMaxPercent = (agents: ChartAgent[], category: string): number => {
  const largestShare = Math.max(
    0,
    ...agents.map((agent) => shareOfFindings(agent, category))
  );
  return largestShare === 0 ? 100 : largestShare * 100 * AXIS_HEADROOM;
};

const buildChartOptions = (
  agents: ChartAgent[],
  category: string,
  unitNoun: string,
  tokens: ThemeTokens,
  onHover: (index: number | null) => void
): ChartOptions<'bar'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  onHover: (_event, elements) => onHover(elements[0]?.index ?? null),
  plugins: {
    legend: { display: false },
    tooltip: {
      position: 'farCorner',
      caretSize: 0,
      callbacks: {
        title: (items) => agents[items[0]?.dataIndex ?? -1]?.name ?? '',
        label: (ctx) => {
          const agent = agents[ctx.dataIndex];
          if (!agent) return '';
          const count = agent.countsByCategory[category] ?? 0;
          return `${count} of ${agent.total} ${unitNoun} (${formatPercent(shareOfFindings(agent, category))})`;
        },
        afterBody: (items) => {
          const agent = agents[items[0]?.dataIndex ?? -1];
          if (!agent) return '';
          const lines: string[] = [];
          if (agents.length > 2) {
            lines.push(
              "Other bars' whiskers now show the 5% comparison against this agent"
            );
          }
          if (agent.rawAgents.length > 1) {
            lines.push('Merged:', ...agent.rawAgents.map((raw) => `  ${raw}`));
          }
          return lines;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: tokens.tick, autoSkip: false, maxRotation: 45 },
    },
    y: {
      min: 0,
      max: axisMaxPercent(agents, category),
      grid: { color: tokens.grid },
      ticks: {
        color: tokens.tick,
        callback: (value) => `${Number(value).toFixed(0)}%`,
      },
    },
  },
});

type PairTest = {
  comparison: RateComparison;
  // Bonferroni: the level a single category must clear given how many were tested
  adjustedLevel: number;
};

const PairTestLine = ({
  agents,
  test,
}: {
  agents: ChartAgent[];
  test: PairTest;
}) => {
  const { comparison, adjustedLevel } = test;
  const clearsLevel = comparison.pValue < SIGNIFICANCE_LEVEL;
  const clearsAdjusted = comparison.pValue < adjustedLevel;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>
        {formatPoints(comparison.difference)} pts for{' '}
        {truncateLabel(agents[0].name)} vs {truncateLabel(agents[1].name)}
      </span>
      <span>
        95% CI {formatPoints(comparison.ciLow)} to{' '}
        {formatPoints(comparison.ciHigh)}
      </span>
      <span>
        {formatPValue(comparison.pValue)}
        {comparison.method === 'fisher' ? ' (exact)' : ''}
      </span>
      {clearsAdjusted ? (
        <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 font-medium text-foreground">
          Differs, even after adjusting for the categories tested
        </span>
      ) : clearsLevel ? (
        <span className="rounded-full border px-2 py-0.5 font-medium text-foreground">
          Differs at 5%, not after adjusting for the categories tested
        </span>
      ) : null}
    </div>
  );
};

type CategoryChartProps = {
  category: string;
  agents: ChartAgent[];
  unitNoun: string;
  tokens: ThemeTokens;
  pairTest: PairTest | null;
};

const CategoryChart = ({
  category,
  agents,
  unitNoun,
  tokens,
  pairTest,
}: CategoryChartProps) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const data = useMemo(
    () => ({
      labels: agents.map((agent) => [
        truncateLabel(agent.name),
        `n = ${agent.total.toLocaleString()}`,
      ]),
      datasets: [
        {
          data: agents.map((agent) => shareOfFindings(agent, category) * 100),
          backgroundColor: agents.map((agent, index) =>
            seriesColor(tokens, index, agent)
          ),
          borderRadius: 4,
          maxBarThickness: 48,
          whiskers: whiskerHalfWidths(
            agents.map((agent) => sampleOf(agent, category)),
            hoveredIndex
          ).map((share) => share * 100),
        },
      ],
    }),
    [agents, category, tokens, hoveredIndex]
  );
  const options = useMemo(
    () =>
      buildChartOptions(agents, category, unitNoun, tokens, setHoveredIndex),
    [agents, category, unitNoun, tokens]
  );
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="truncate text-sm font-medium" title={category}>
          {category}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-75" onMouseLeave={() => setHoveredIndex(null)}>
          <Bar data={data} options={options} />
        </div>
        {pairTest && <PairTestLine agents={agents} test={pairTest} />}
      </CardContent>
    </Card>
  );
};

type AgentCategoryChartsProps = {
  counts: OnlineEvalAgentCountsResponse | null;
  loading: boolean;
  // True while the counts shown are for the previous filters and the new ones are loading
  stale: boolean;
  error: string | null;
  // The agents and category IDs the filters select, which the counts ignore. Without a
  // selection, the charts show the most common agents and the most telling categories.
  agentFilter: string[];
  categoryIdFilter: string[];
  settings: OnlineEvalChartSettings;
  onSettingsChange: (settings: OnlineEvalChartSettings) => void;
};

export const AgentCategoryCharts = ({
  counts,
  loading,
  stale,
  error,
  agentFilter,
  categoryIdFilter,
  settings,
  onSettingsChange,
}: AgentCategoryChartsProps) => {
  const theme = useTheme();
  const tokens = TOKENS[theme];
  const update = (change: Partial<OnlineEvalChartSettings>) =>
    onSettingsChange({ ...settings, ...change });

  const unit = UNIT_COPY[settings.chartUnit];
  const { agents, patternError } = useMemo(() => {
    const keepsAgent = (agent: string) =>
      agentFilter.length === 0 || agentFilter.includes(agent);
    const keepsCategory = (categoryId: string) =>
      categoryIdFilter.length === 0 || categoryIdFilter.includes(categoryId);
    return coalesceAgents(
      (counts?.agent_counts ?? [])
        .filter((row) => keepsAgent(row.agent))
        .map((row) => ({ agent: row.agent, count: unit.countOf(row) })),
      (counts?.agent_category_counts ?? [])
        .filter(
          (row) => keepsAgent(row.agent) && keepsCategory(row.category_id)
        )
        .map((row) => ({
          agent: row.agent,
          category: row.category_name,
          count: unit.countOf(row),
        })),
      settings.agentStrip
    );
  }, [counts, settings.agentStrip, unit, agentFilter, categoryIdFilter]);
  const selectedAgents = useMemo(
    () =>
      agentFilter.length > 0
        ? agents
        : agents.slice(0, DEFAULT_CHART_AGENT_COUNT),
    [agents, agentFilter]
  );
  const isPair = selectedAgents.length === 2;
  const rankedCategories = useMemo(
    () =>
      isPair
        ? rankCategoriesByComparison(selectedAgents[0], selectedAgents[1])
        : rankCategoriesByDiscrimination(agents),
    [agents, selectedAgents, isPair]
  );
  const selectedCategories =
    categoryIdFilter.length > 0
      ? rankedCategories
      : rankedCategories.slice(0, DEFAULT_CHART_CATEGORY_COUNT);
  const adjustedLevel =
    SIGNIFICANCE_LEVEL / Math.max(1, rankedCategories.length);
  const pairTestFor = (category: string): PairTest | null => {
    if (!isPair) return null;
    const comparison = compareRates(
      sampleOf(selectedAgents[0], category),
      sampleOf(selectedAgents[1], category)
    );
    return comparison && { comparison, adjustedLevel };
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">Agent comparison</CardTitle>
              {stale && (
                <span
                  role="status"
                  className="flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground"
                >
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  Loading the new filters; the charts below show the previous
                  ones
                </span>
              )}
            </div>
            <SegmentButton
              options={(['chat', 'message'] as const).map((value) => ({
                value,
                label: UNIT_COPY[value].label,
              }))}
              selected={settings.chartUnit}
              onSelect={(chartUnit) => update({ chartUnit })}
            />
          </div>
          <CardDescription>
            {unit.description} At rest each whisker is an 83% confidence
            interval, sized so that two overlapping whiskers mean the rates do
            not differ at the 5% level, provided the bars have similar standard
            errors. Hover a bar and every other whisker becomes an exact
            comparison against it, with no such proviso; with two agents the
            whiskers are always that comparison. Faded bars have fewer than{' '}
            {SMALL_SAMPLE} {unit.noun}, where the printed p-value is the better
            guide. The filters above apply here too. With no agent selected, the
            charts show the {DEFAULT_CHART_AGENT_COUNT} most common agents. With
            no category selected, they show the {DEFAULT_CHART_CATEGORY_COUNT}{' '}
            categories where the agents differ most, or, with two agents, where
            they differ most confidently. The controls below change only these
            charts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
            <label className="flex min-w-0 flex-1 flex-col gap-2 sm:min-w-[220px]">
              <span className="text-xs font-semibold uppercase text-muted-foreground">
                Strip regex from agent names
              </span>
              <Input
                placeholder="Regex, like baseten|fireworks"
                value={settings.agentStrip}
                onChange={(event) => update({ agentStrip: event.target.value })}
              />
              {patternError ? (
                <span className="text-xs text-destructive">{patternError}</span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Every match is removed; agents with the same name after
                  stripping merge into one bar
                </span>
              )}
            </label>
          </div>
        </CardContent>
      </Card>

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      ) : loading && counts === null ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Loading agent comparison charts…
        </div>
      ) : selectedCategories.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No {unit.noun} match the current filters. Both views count what the
          evaluator has judged since evaluated messages started being recorded,
          so a window before then has nothing to compare.
        </div>
      ) : (
        <div
          aria-busy={stale}
          className={cn(
            'grid gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3',
            stale && 'opacity-40'
          )}
        >
          {selectedCategories.map((category) => (
            <CategoryChart
              key={category}
              category={category}
              agents={selectedAgents}
              unitNoun={unit.noun}
              tokens={tokens}
              pairTest={pairTestFor(category)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
